import { useState, useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { aiService, ConsultationContext, AISuggestion, getCurrentModelInfo } from '../services/aiService'
import './GeminiSuggestions.css'

interface GeminiSuggestionsProps {
  context: ConsultationContext
  onSuggestionClick?: (suggestion: AISuggestion) => void
}

// Debounce delay in milliseconds
const DEBOUNCE_DELAY = 3000 // 3 seconds after last change
const MIN_CHARACTERS = 20 // Minimum characters before making a request

export function GeminiSuggestions({ context, onSuggestionClick }: GeminiSuggestionsProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const [debouncedContext, setDebouncedContext] = useState<ConsultationContext | null>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout>()
  const contextRef = useRef<ConsultationContext>(context)

  // Keep context ref up to date
  useEffect(() => {
    contextRef.current = context
  }, [context])

  // Check current content length
  const notes = context.notes || ''
  const reason = context.reasonForVisit || ''
  const totalLength = notes.length + reason.length
  const hasEnoughContent = totalLength >= MIN_CHARACTERS
  const hasContent = !!(context.notes || context.reasonForVisit)
  const isTyping = hasContent && !hasEnoughContent

  // Create a stable key for the context to detect changes
  const contextKey = useMemo(() => {
    return JSON.stringify({
      notes: context.notes || '',
      reason: context.reasonForVisit || '',
      diagnosis: context.diagnosis?.join(',') || '',
      patientAge: context.patientAge,
      patientGender: context.patientGender,
    })
  }, [context.notes, context.reasonForVisit, context.diagnosis, context.patientAge, context.patientGender])

  // Debounce context updates
  useEffect(() => {
    console.log('[GeminiSuggestions] Effect triggered', { 
      contextKey, 
      hasEnoughContent, 
      notesLength: notes.length, 
      reasonLength: reason.length 
    })

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Only proceed if we have enough content
    if (!hasEnoughContent) {
      // Clear debounced context if we drop below minimum
      setDebouncedContext(null)
      return
    }

    // Set new timer to update debounced context
    // Use ref to get latest context value when timer fires
    debounceTimerRef.current = setTimeout(() => {
      const latestContext = { ...contextRef.current }
      console.log('[GeminiSuggestions] Updating debounced context after delay', { 
        notesLength: latestContext.notes?.length || 0, 
        reasonLength: latestContext.reasonForVisit?.length || 0,
        totalLength: (latestContext.notes?.length || 0) + (latestContext.reasonForVisit?.length || 0)
      })
      setDebouncedContext(latestContext)
    }, DEBOUNCE_DELAY)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [contextKey, hasEnoughContent]) // Only depend on the key and hasEnoughContent

  // Check if debounced context has enough content
  const debouncedHasEnoughContent = useMemo(() => {
    if (!debouncedContext) return false
    const debouncedNotes = debouncedContext.notes || ''
    const debouncedReason = debouncedContext.reasonForVisit || ''
    const hasEnough = (debouncedNotes.length + debouncedReason.length) >= MIN_CHARACTERS
    console.log('[GeminiSuggestions] Checking debounced content', {
      hasDebouncedContext: !!debouncedContext,
      debouncedNotesLength: debouncedNotes.length,
      debouncedReasonLength: debouncedReason.length,
      hasEnough,
    })
    return hasEnough
  }, [debouncedContext])

  // Create a stable query key from debounced context
  const queryKey = useMemo(() => {
    if (!debouncedContext) return null
    return [
      'gemini-suggestions',
      debouncedContext.notes?.slice(0, 50) || '',
      debouncedContext.reasonForVisit?.slice(0, 50) || '',
      debouncedContext.diagnosis?.join(',') || '',
    ]
  }, [debouncedContext])

  const { data: suggestions, isLoading, error, refetch } = useQuery({
    queryKey: queryKey || ['gemini-suggestions', 'disabled'],
    queryFn: () => {
      if (!debouncedContext) {
        throw new Error('No context available')
      }
      console.log('[GeminiSuggestions] Making API request with context', {
        notesLength: debouncedContext.notes?.length || 0,
        reasonLength: debouncedContext.reasonForVisit?.length || 0,
      })
      return aiService.getSuggestions(debouncedContext)
    },
    enabled: !!debouncedContext && debouncedHasEnoughContent && !!queryKey,
    staleTime: 60000, // Cache for 60 seconds
    gcTime: 300000, // Keep in cache for 5 minutes
  })

  // Get current model info for display
  const modelInfo = useMemo(() => getCurrentModelInfo(), [])

  const getSuggestionIcon = (type: AISuggestion['type']) => {
    switch (type) {
      case 'diagnosis':
        return '🔍'
      case 'treatment':
        return '💊'
      case 'test':
        return '🧪'
      case 'referral':
        return '🏥'
      case 'medication':
        return '💉'
      case 'followup':
        return '📅'
      default:
        return '💡'
    }
  }

  const getConfidenceBadge = (confidence?: string) => {
    if (!confidence) return null
    const classes = {
      high: 'confidence-high',
      medium: 'confidence-medium',
      low: 'confidence-low',
    }
    return (
      <span className={`confidence-badge ${classes[confidence as keyof typeof classes] || ''}`}>
        {confidence}
      </span>
    )
  }


  return (
    <div className="gemini-suggestions-content">
      <div className="gemini-suggestions-header">
        <div className="header-left">
          <span className="gemini-icon">🤖</span>
          <div className="header-title-group">
            <h3>AI Suggestions</h3>
            <span className="model-info">{modelInfo.provider === 'gemini' ? 'Gemini' : 'ChatGPT'} • {modelInfo.model}</span>
          </div>
          {isTyping && (
            <span className="typing-indicator">
              {MIN_CHARACTERS - totalLength} more chars...
            </span>
          )}
          {isLoading && hasEnoughContent && <span className="loading-indicator">Analyzing...</span>}
          {suggestions && suggestions.length > 0 && (
            <span className="suggestions-count">{suggestions.length}</span>
          )}
        </div>
        <div className="header-actions">
          {hasEnoughContent && (
            <button
              className="btn-icon"
              onClick={() => refetch()}
              disabled={isLoading}
              title="Refresh suggestions"
            >
              🔄
            </button>
          )}
          <button
            className="btn-icon"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        </div>
      </div>

      {error && (
        <div className="gemini-error">
          <p className="error-message">Failed to load suggestions.</p>
          <button className="btn-link-small" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}

      {isExpanded && !error && (
        <div className="suggestions-content">
          {!hasContent ? (
            <div className="suggestions-empty">
              <p>Add consultation notes or a reason for visit to get AI-powered suggestions.</p>
              <p className="hint">The AI will analyze patient history, medications, and clinical notes to provide relevant recommendations.</p>
            </div>
          ) : isTyping ? (
            <div className="suggestions-empty">
              <p>Keep typing... ({totalLength}/{MIN_CHARACTERS} characters)</p>
              <p className="hint">AI suggestions will appear after {MIN_CHARACTERS} characters and {DEBOUNCE_DELAY / 1000} seconds of inactivity.</p>
            </div>
          ) : isLoading ? (
            <div className="suggestions-loading">
              <p>Analyzing consultation notes and patient history...</p>
            </div>
          ) : suggestions && suggestions.length > 0 ? (
            <div className="suggestions-list">
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="suggestion-item"
                  onClick={() => onSuggestionClick?.(suggestion)}
                >
                  <div className="suggestion-icon">{getSuggestionIcon(suggestion.type)}</div>
                  <div className="suggestion-content">
                    <div className="suggestion-header">
                      <h4>{suggestion.title}</h4>
                      {getConfidenceBadge(suggestion.confidence)}
                    </div>
                    <p className="suggestion-description">{suggestion.description}</p>
                    <span className="suggestion-type">{suggestion.type}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="suggestions-empty">
              <p>No suggestions available at this time.</p>
              <p className="hint">Add more notes or details to get AI suggestions.</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}






























